/**
 * How a job's status reads on screen.
 *
 * Two things were wrong here at once. The backend writes ACTIVE and PHANTOM,
 * and neither had an entry — so the single most common status in the factory
 * fell through to the unknown-status fallback and rendered grey. Half the
 * Projects table was therefore painted the colour reserved for "we don't know
 * what this is", and it was the half that is actually running.
 *
 * The other thing: nothing here is grey any more. Grey reads as disabled or
 * absent, and every one of these is a real state somebody acts on. An unknown
 * status now comes out amber — if a new status appears and nobody maps it, that
 * should look like something to look at, not like nothing.
 */
const JOB_STATUS = {
  PENDING: { label: 'pending', type: 'warning', uiLabel: 'Pending' },
  ACTIVE: { label: 'active', type: 'primary', uiLabel: 'Active' },
  RUNNING: { label: 'running', type: 'primary', uiLabel: 'Running' },
  // A phantom job consumes stock instead of running a machine. It is neither
  // waiting nor being made, so it gets its own colour rather than borrowing one.
  PHANTOM: { label: 'phantom', type: 'info', uiLabel: 'Phantom' },
  INACTIVE: { label: 'inactive', type: 'warning', uiLabel: 'Inactive' },
  CRITICAL: { label: 'critical', type: 'danger', uiLabel: 'Critical' },
  COMPLETED: { label: 'completed', type: 'success', uiLabel: 'Completed' },
  DONE: { label: 'completed', type: 'success', uiLabel: 'Completed' },
  CANCELLED: { label: 'cancelled', type: 'danger', uiLabel: 'Cancelled' }
};

/**
 * Two different shapes reach this, and both are legitimate.
 *
 * Most screens hand it the raw status string off the job. But
 * `jobsAdapter.adaptJobToProjectRow` calls this itself and stores the RESULT on
 * the row, so the list and card views hand back an already-resolved object —
 * and a shared component cannot know which of its callers it is serving.
 * Passing the object through the string path threw
 * `(intermediate value).toLowerCase is not a function` and took the whole page
 * down with it.
 *
 * Resolving an already-resolved status is the identity, so it simply says so.
 */
export const getJobStatusUI = status => {
  if (status && typeof status === 'object') return status;

  return (
    JOB_STATUS[String(status || '').toUpperCase()] || {
      label: String(status || 'unknown').toLowerCase(),
      type: 'warning',
      uiLabel: status || 'Unknown'
    }
  );
};
